import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Loader2, CheckCheck, Tag, X, Check, AlertTriangle, TrendingUp, Clock, ArrowLeft, Calendar, Shield, EyeOff } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Producer-wide inquiries dashboard. Aggregates ALL inquiries across ALL events
 * with KPIs (close rate, avg response time) and an urgency-first sorted list.
 */
export default function ProducerInquiriesDashboardPage() {
  const [data, setData] = useState({ inquiries: [], metrics: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState({ inquiry: null, messages: [] });
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/producers/me/inquiries/dashboard');
      setData(res.data || { inquiries: [], metrics: null });
    } catch (err) {
      toast.error('No se pudo cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (id) => {
    setLoadingThread(true);
    try {
      const res = await api.get(`/producers/me/inquiries/${id}`);
      setThread(res.data);
      loadDashboard(); // refresh unread counters
    } catch (err) {
      toast.error('No se pudo cargar la consulta');
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => {
    if (selectedId) loadThread(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread.messages]);

  const sendReply = async (e) => {
    e.preventDefault();
    const body = reply.trim();
    if (!body || !selectedId) return;
    setSending(true);
    try {
      const res = await api.post(`/producers/me/inquiries/${selectedId}/messages`, { message: body });
      setThread((prev) => ({ ...prev, messages: [...prev.messages, res.data.message] }));
      setReply('');
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (!selectedId) return;
    try {
      await api.put(`/producers/me/inquiries/${selectedId}/status`, { status });
      if (status === 'sold' && thread.inquiry?.tier_id && thread.inquiry?.event_id) {
        await api.put(`/producers/me/events/${thread.inquiry.event_id}/tiers/${thread.inquiry.tier_id}/sold-out?is_sold_out=true`);
        toast.success('Marcada como vendida + tanda agotada');
      } else {
        toast.success('Estado actualizado');
      }
      setThread((prev) => ({ ...prev, inquiry: { ...prev.inquiry, status } }));
      loadDashboard();
    } catch (err) {
      toast.error('No se pudo actualizar');
    }
  };

  const approveComment = async () => {
    if (!selectedId) return;
    try {
      await api.put(`/producers/me/inquiries/${selectedId}/approve`);
      toast.success('Comentario aprobado, ya es visible');
      setThread((prev) => ({ ...prev, inquiry: { ...prev.inquiry, status: 'open' } }));
      loadDashboard();
    } catch (err) {
      toast.error('No se pudo aprobar');
    }
  };

  const rejectComment = async () => {
    if (!selectedId) return;
    try {
      await api.put(`/producers/me/inquiries/${selectedId}/reject`);
      toast.success('Comentario rechazado');
      setThread((prev) => ({ ...prev, inquiry: { ...prev.inquiry, status: 'hidden' } }));
      loadDashboard();
    } catch (err) {
      toast.error('No se pudo rechazar');
    }
  };

  const m = data.metrics;
  const autoRejectedCount = (data.inquiries || []).filter((q) => q.auto_rejected).length;
  const STATUS_OPTIONS = [
    { id: 'all', label: 'Todas', count: m?.total },
    { id: 'urgent', label: '🔴 Urgentes', count: m?.urgent_count },
    { id: 'pending_approval', label: '🛡️ Por aprobar', count: m?.by_status?.pending_approval },
    { id: 'auto_rejected', label: '🤖 Spam detectado', count: autoRejectedCount },
    { id: 'open', label: 'Pendientes', count: m?.by_status?.open },
    { id: 'answered', label: 'Respondidas', count: m?.by_status?.answered },
    { id: 'sold', label: 'Vendidas', count: m?.by_status?.sold },
    { id: 'closed', label: 'Cerradas', count: m?.by_status?.closed },
  ];

  const filteredInquiries = data.inquiries.filter((q) => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return q.is_urgent;
    if (filter === 'auto_rejected') return !!q.auto_rejected;
    return q.status === filter;
  });

  const formatResponseTime = (mins) => {
    if (mins === null || mins === undefined) return '—';
    if (mins < 60) return `${Math.round(mins)} min`;
    if (mins < 60 * 24) return `${(mins / 60).toFixed(1)} h`;
    return `${(mins / 60 / 24).toFixed(1)} d`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-zinc-900/40 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/producer/dashboard" className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft size={18} />
          </Link>
          <div className="flex-1">
            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <MessageCircle size={20} className="text-violet-400" /> Pipeline de Consultas
            </h1>
            <p className="text-xs text-white/50">Todas las consultas, de todos tus eventos.</p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<MessageCircle size={16} className="text-white/40" />}
            label="Total"
            value={m?.total ?? '—'}
            sublabel="consultas históricas"
            testid="kpi-total"
          />
          <KpiCard
            icon={<AlertTriangle size={16} className="text-red-400" />}
            label="Urgentes"
            value={m?.urgent_count ?? '—'}
            sublabel="pendientes +24hs"
            accent={(m?.urgent_count || 0) > 0 ? 'red' : 'neutral'}
            testid="kpi-urgent"
          />
          <KpiCard
            icon={<TrendingUp size={16} className="text-emerald-400" />}
            label="Tasa de cierre"
            value={m ? `${(m.close_rate * 100).toFixed(0)}%` : '—'}
            sublabel={`${m?.by_status?.sold ?? 0} vendidas`}
            accent="emerald"
            testid="kpi-close-rate"
          />
          <KpiCard
            icon={<Clock size={16} className="text-violet-400" />}
            label="Respuesta promedio"
            value={formatResponseTime(m?.avg_response_minutes)}
            sublabel="primera respuesta"
            accent="violet"
            testid="kpi-avg-response"
          />
        </div>
      </div>

      {/* List + Thread */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
          {/* List column */}
          <div className="md:col-span-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-zinc-800 overflow-x-auto whitespace-nowrap scrollbar-thin">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => { setFilter(opt.id); setSelectedId(null); }}
                  className={`text-[11px] px-2.5 py-1 rounded-full mr-1.5 transition-colors ${filter === opt.id ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
                  data-testid={`dash-filter-${opt.id}`}
                >
                  {opt.label} {opt.count != null && <span className="opacity-60">· {opt.count}</span>}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-zinc-500"><Loader2 className="animate-spin mx-auto" size={18} /></div>
              ) : filteredInquiries.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 text-xs">No hay consultas en este filtro.</div>
              ) : (
                filteredInquiries.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => setSelectedId(q.id)}
                    className={`w-full text-left px-3 py-3 border-b border-zinc-800 hover:bg-white/5 transition-colors ${selectedId === q.id ? 'bg-violet-500/10' : ''}`}
                    data-testid={`dash-inq-${q.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${q.status === 'pending_approval' ? 'bg-orange-400' : q.status === 'open' ? 'bg-amber-400' : q.status === 'answered' ? 'bg-violet-400' : q.status === 'sold' ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                      <p className="text-sm font-semibold text-white truncate flex-1">{q.buyer_name}</p>
                      {q.auto_rejected && (
                        <span className="text-[9px] bg-red-500/80 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider" title={q.spam_reason || 'Detectado como spam'}>🤖 Spam</span>
                      )}
                      {q.is_public && !q.auto_rejected && (
                        <span className="text-[9px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">Público</span>
                      )}
                      {q.status === 'pending_approval' && (
                        <span className="text-[9px] bg-orange-500 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">Por aprobar</span>
                      )}
                      {q.is_urgent && q.status !== 'pending_approval' && (
                        <span className="text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">+24h</span>
                      )}
                      {q.unread_count_producer > 0 && q.status !== 'pending_approval' && (
                        <span className="text-[10px] bg-pink-500 text-white px-1.5 py-0.5 rounded-full font-bold">{q.unread_count_producer}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400 truncate flex items-center gap-1">
                      <Tag size={9} className="opacity-60" /> {q.tier_name}
                    </p>
                    <p className="text-[10px] text-violet-300 truncate flex items-center gap-1 mt-0.5">
                      <Calendar size={9} className="opacity-80" /> {q.event_title}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {new Date(q.last_message_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Thread column */}
          <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col">
            {!selectedId ? (
              <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm text-center px-6">
                Seleccioná una consulta para responder. Las urgentes (sin responder +24hs) aparecen primero.
              </div>
            ) : loadingThread ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>
            ) : thread.inquiry ? (
              <>
                <div className="px-4 py-3 border-b border-zinc-800 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{thread.inquiry.buyer_name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{thread.inquiry.buyer_email}{thread.inquiry.buyer_phone && ` · ${thread.inquiry.buyer_phone}`}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Link to={`/producer/events/${thread.inquiry.event_id}`} className="text-[10px] text-violet-300 hover:underline truncate">
                        <Calendar size={9} className="inline mr-0.5" /> {thread.inquiry.event_title}
                      </Link>
                      <span className="text-[10px] text-zinc-400">
                        <Tag size={9} className="inline mr-0.5" /> {thread.inquiry.tier_name}
                      </span>
                    </div>
                    {thread.inquiry.auto_rejected && (
                      <div className="mt-2 text-[11px] bg-red-500/10 border border-red-500/30 rounded-md px-2 py-1.5 text-red-200 flex items-start gap-1.5">
                        <span>🤖</span>
                        <div className="flex-1">
                          <p className="font-semibold">Detectado como spam por el clasificador automático.</p>
                          <p className="opacity-80">
                            {thread.inquiry.spam_reason || thread.inquiry.spam_category || 'Patrón sospechoso'}
                            {typeof thread.inquiry.spam_confidence === 'number' && (
                              <> · confianza {(thread.inquiry.spam_confidence * 100).toFixed(0)}%</>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {thread.inquiry.is_public && (thread.inquiry.status === 'pending_approval' || thread.inquiry.auto_rejected) && (
                      <button onClick={approveComment} title="Aprobar comentario y publicar"
                        className="text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1 rounded-md font-semibold flex items-center gap-1"
                        data-testid="dash-approve-comment">
                        <Shield size={11} /> Aprobar
                      </button>
                    )}
                    {thread.inquiry.is_public && thread.inquiry.status === 'pending_approval' && !thread.inquiry.auto_rejected && (
                      <button onClick={rejectComment} title="Rechazar comentario (no será visible)"
                        className="text-[10px] bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md font-semibold flex items-center gap-1"
                        data-testid="dash-reject-comment">
                        <EyeOff size={11} /> Rechazar
                      </button>
                    )}
                    {thread.inquiry.is_public && thread.inquiry.status === 'open' && (
                      <button onClick={rejectComment} title="Ocultar comentario"
                        className="text-[10px] bg-white/10 hover:bg-white/15 text-white px-2 py-1 rounded-md flex items-center gap-1"
                        data-testid="dash-hide-comment">
                        <EyeOff size={11} /> Ocultar
                      </button>
                    )}
                    {!thread.inquiry.is_public && thread.inquiry.status !== 'sold' && (
                      <button onClick={() => setStatus('sold')} title="Marcar vendida + agotar tanda"
                        className="text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1 rounded-md font-semibold flex items-center gap-1"
                        data-testid="dash-mark-sold">
                        <Check size={11} /> Vendida
                      </button>
                    )}
                    {thread.inquiry.status !== 'closed' && thread.inquiry.status !== 'pending_approval' && (
                      <button onClick={() => setStatus('closed')} title="Cerrar"
                        className="text-[10px] bg-white/10 hover:bg-white/15 text-white px-2 py-1 rounded-md flex items-center gap-1">
                        <X size={11} /> Cerrar
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
                  {thread.messages.map((msg) => {
                    const fromProducer = msg.sender_type === 'producer';
                    return (
                      <motion.div key={msg.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className={`flex ${fromProducer ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${fromProducer ? 'bg-violet-500 text-white' : 'bg-white/5 border border-white/10 text-white'}`}>
                          <p className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{msg.sender_name}</p>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                          <p className="text-[10px] opacity-50 mt-1 flex items-center gap-1 justify-end">
                            {new Date(msg.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            {fromProducer && msg.read_at && <CheckCheck size={11} />}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                {thread.inquiry.status !== 'closed' ? (
                  <form onSubmit={sendReply} className="border-t border-zinc-800 px-4 py-3 bg-zinc-900">
                    <div className="flex items-end gap-2">
                      <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={1}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(e); } }}
                        placeholder="Responder…"
                        maxLength={2000}
                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none resize-none max-h-32"
                        data-testid="dash-reply-input"
                      />
                      <button type="submit" disabled={sending || !reply.trim()}
                        className="p-2 rounded-full bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-40"
                        data-testid="dash-reply-send">
                        <Send size={14} />
                      </button>
                    </div>
                    {/* Live character counter — turns amber at 1800 and red at the 2000 hard cap */}
                    <div
                      className={`mt-1.5 text-[10px] font-mono tabular-nums text-right transition-colors ${
                        reply.length >= 2000
                          ? 'text-red-400'
                          : reply.length >= 1800
                            ? 'text-amber-400'
                            : 'text-white/30'
                      }`}
                      data-testid="dash-reply-char-counter"
                      aria-live="polite"
                    >
                      {reply.length}/2000
                    </div>
                  </form>
                ) : (
                  <div className="border-t border-zinc-800 px-4 py-3 text-center text-[11px] text-zinc-500">Conversación cerrada</div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sublabel, accent = 'neutral', testid }) {
  const accentBorder = {
    red: 'border-red-500/40 bg-red-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    violet: 'border-violet-500/30 bg-violet-500/5',
    neutral: 'border-white/10 bg-white/[0.02]',
  }[accent];
  return (
    <div className={`rounded-xl border ${accentBorder} p-3.5`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <p className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sublabel && <p className="text-[10px] text-white/40 mt-0.5">{sublabel}</p>}
    </div>
  );
}
